import gql from 'graphql-tag';
import React, {useState} from 'react';

import {assertAndContinue} from 'Asserts';
import SourceDAO from 'file-browser/SourceDAO';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import {ButtonStyle} from 'ui-library/buttons/Button';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import {OptionData} from 'ui-library/menus/MenuConstants';
import Modal from 'ui-library/modals/Modal';
import {withLoadingScreen} from 'util/Promises';

import {
  OrganizationReviewLifecyclesQuery,
  OrganizationReviewLifecyclesQueryVariables,
} from './__generated/OrganizationReviewLifecyclesQuery';
import {
  ProjectOrganizationsQuery,
  ProjectOrganizationsQueryVariables,
} from './__generated/ProjectOrganizationsQuery';
import ReviewProcessSelector, {generateReviewProcessSelectorOptions} from './ReviewProcessSelector';

type ProjectOrInaccessibleGraphQL = ProjectOrganizationsQuery['projects'][number];

type ProjectGraphQL = ExtractByTypename<ProjectOrInaccessibleGraphQL, 'ProjectNode'>;

export const PROJECT_ORGANIZATIONS_QUERY = gql`
  query ProjectOrganizationsQuery($projectIds: [StrictString!]!) @skipAudit @engComponent(name: NOTEBOOK) {
    projects(ids: $projectIds) {
      ... on ProjectNode {
        id
        ownerJson
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const ORGANIZATION_REVIEW_LIFECYCLES_QUERY = gql`
  query OrganizationReviewLifecyclesQuery($organizationId: StrictString!)
  @skipAudit
  @engComponent(name: NOTEBOOK) {
    organization(id: $organizationId) {
      ... on OrganizationNode {
        id
        reviewLifecycles {
          id
          currentVersion {
            id
            name
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface BulkUpdateProjectReviewLifecycleModalProps {
  projectIds: Array<string>;
  onRequestClose: () => void;
}

interface ReassignProjectsReviewLifecycleModalProps {
  organizationId: string;
  projectIds: Array<string>;
  onRequestClose: () => void;
}

function ReassignProjectsReviewLifecycleModal({
  organizationId,
  projectIds,
  onRequestClose,
}: ReassignProjectsReviewLifecycleModalProps): JSX.Element | null {
  const result = useBenchlingQuery<
    OrganizationReviewLifecyclesQuery,
    OrganizationReviewLifecyclesQueryVariables
  >(ORGANIZATION_REVIEW_LIFECYCLES_QUERY, {
    variables: {organizationId},
    fetchPolicy: 'network-only',
  });

  const [isAuditorBasedReview, setIsAuditorBasedReview] = useState(false);
  const [selectedReviewLifecycleIds, setSelectedReviewLifecycleIds] = useState<Array<string>>([]);

  function selectReviewLifecycleId(reviewLifecycleId: string): void {
    setSelectedReviewLifecycleIds((previouslySelected) => {
      if (!previouslySelected.includes(reviewLifecycleId)) {
        return previouslySelected.concat([reviewLifecycleId]);
      }
      return previouslySelected.filter((rlcId) => rlcId !== reviewLifecycleId);
    });
  }

  function removeReviewLifecycleId(reviewLifecycleId: string): void {
    setSelectedReviewLifecycleIds(selectedReviewLifecycleIds.filter((id) => id !== reviewLifecycleId));
  }

  if (result.type === QueryResultType.PENDING) {
    return <HorizontalThrobber />;
  } else if (result.type === QueryResultType.ERROR) {
    return null;
  }

  const onRequestSubmit = async (): Promise<void> => {
    window.onbeforeunload = () => '';
    await withLoadingScreen(
      SourceDAO.bulkSetProjectsReviewLifecycle({
        projectApiIds: projectIds,
        reviewLifecycleApiIds: selectedReviewLifecycleIds,
      })
    );
    notifications.success('Successfully updated review process on selected projects.');
    window.onbeforeunload = null;
    onRequestClose();
  };

  const {organization} = result.data;
  if (isInaccessible(organization)) {
    assertAndContinue(false, 'User did not have access to the organization');
    return null;
  }

  const reviewProcessOptions: Array<OptionData> = generateReviewProcessSelectorOptions({
    reviewLifecycleIdAndNames: organization.reviewLifecycles.map((reviewLifecycle) => ({
      id: reviewLifecycle.id,
      name: reviewLifecycle.currentVersion.name,
    })),
    selectedReviewLifecycleIds,
    isMulti: true,
  });

  return (
    <Modal
      closeable={true}
      title="Change review processes for the selected projects"
      onRequestClose={onRequestClose}
      onRequestSubmit={onRequestSubmit}
      buttons={[
        {
          text: 'Cancel',
          cancel: true,
          style: ButtonStyle.TERTIARY_GRAY,
        },
        {
          text: 'Update',
          submit: true,
          style: ButtonStyle.SECONDARY,
          disabled: selectedReviewLifecycleIds.length === 0 && !isAuditorBasedReview,
        },
      ]}
    >
      <div style={{marginBottom: '8px'}}>
        The new selected options will replace current project configuration for the selected projects
        entirely.
      </div>
      <ReviewProcessSelector
        includeAuditorBasedReviewOption={true}
        isAuditorBasedReview={isAuditorBasedReview}
        setIsAuditorBasedReview={setIsAuditorBasedReview}
        isMulti={true}
        showWarnings={false}
        selectReviewLifecycleId={(reviewLifecyleId) => {
          selectReviewLifecycleId(reviewLifecyleId);
        }}
        removeReviewLifecycleId={removeReviewLifecycleId}
        options={reviewProcessOptions}
        selectedReviewLifecycleIds={selectedReviewLifecycleIds}
      />
    </Modal>
  );
}

function areProjectsAccessible(
  projects: Array<ProjectOrInaccessibleGraphQL>
): projects is Array<ProjectGraphQL> {
  for (const project of projects) {
    if (isInaccessible(project)) {
      assertAndContinue(false, 'User did not have access to a project');
      return false;
    }
    if (project.ownerJson.type !== 'organization') {
      notifications.error('Review processes are not supported for user-owned projects.');
      return false;
    }
  }
  return true;
}

export default function BulkUpdateProjectReviewLifecycleModal({
  projectIds,
  onRequestClose,
}: BulkUpdateProjectReviewLifecycleModalProps): JSX.Element | null {
  const result = useBenchlingQuery<ProjectOrganizationsQuery, ProjectOrganizationsQueryVariables>(
    PROJECT_ORGANIZATIONS_QUERY,
    {
      variables: {projectIds},
      fetchPolicy: 'network-only',
    }
  );

  if (result.type === QueryResultType.PENDING) {
    return <HorizontalThrobber />;
  } else if (result.type === QueryResultType.ERROR) {
    return null;
  }

  const {projects} = result.data;

  if (!areProjectsAccessible(projects)) {
    return null;
  }

  // Dedupe organization ids
  const organizationIds = new Set(projects.map((project) => project.ownerJson.id));

  if (organizationIds.size !== 1) {
    notifications.error('All selected projects must belong to the same organization.');
    return null;
  }

  return (
    <ReassignProjectsReviewLifecycleModal
      organizationId={[...organizationIds][0]}
      projectIds={projectIds}
      onRequestClose={onRequestClose}
    />
  );
}
